import { get, post, put, del } from "@/shared/utils/fetchApi";

export type BilingualString = { en: string; pt: string };

export type DashboardTemplateScope = "superadmin" | "organization" | "product";

export type ChartType =
  | "line"
  | "bar"
  | "scatter"
  | "area"
  | "pie"
  | "donut"
  | "gauge"
  | "gauge-multiple";

export interface DashboardTemplate {
  id: string;
  name: BilingualString | string;
  description?: BilingualString | string;
  category?: string;
  icon?: string;
  type: ChartType;
  scope: DashboardTemplateScope;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data_sources?: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  default_filters?: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  transform: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  refresh_config?: any;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateTemplateInput {
  name: BilingualString | string;
  description?: BilingualString | string;
  category?: string;
  icon?: string;
  type: ChartType;
  scope: DashboardTemplateScope;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data_sources?: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  default_filters?: any[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  transform: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  refresh_config?: any;
  is_active?: boolean;
}

export type UpdateTemplateInput = Partial<CreateTemplateInput>;

export interface MetricParamSpec {
  name: string;
  type: "string" | "number" | "boolean" | "date";
  description: string;
  required?: boolean;
  enum?: string[];
}

export interface MetricCatalogItem {
  id: string;
  label: string;
  description: string;
  scope: "superadmin" | "organization" | "product" | "any";
  params: MetricParamSpec[];
}

export interface PublishSummary {
  templateId: string;
  scope: DashboardTemplateScope;
  targets: number;
  installed: number;
  skipped: number;
  failed: number;
}

export interface PreviewResponse {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dashboard: { id: string; name?: any; type?: string; transform?: any };
}

export class DashboardTemplatesAdminService {
  static async list(filter?: {
    scope?: DashboardTemplateScope;
    is_active?: boolean;
  }): Promise<DashboardTemplate[]> {
    const qs = new URLSearchParams();
    if (filter?.scope) qs.append("scope", filter.scope);
    if (filter?.is_active !== undefined)
      qs.append("is_active", String(filter.is_active));
    const suffix = qs.toString() ? `?${qs.toString()}` : "";
    // v2: array flat (legacy {success,data}). Degrada p/ [] em prod (tabela ausente).

    return (
      (await get<DashboardTemplate[]>(`/dashboard-templates${suffix}`)) ?? []
    );
  }

  static async getById(id: string): Promise<DashboardTemplate> {
    return get<DashboardTemplate>(`/dashboard-templates/${id}`);
  }

  static async create(input: CreateTemplateInput): Promise<DashboardTemplate> {
    return post<DashboardTemplate>(`/dashboard-templates`, input);
  }

  static async update(
    id: string,
    input: UpdateTemplateInput,
  ): Promise<DashboardTemplate> {
    return put<DashboardTemplate>(`/dashboard-templates/${id}`, input);
  }

  // 204 No Content: o endpoint nao devolve o template apagado.
  static async softDelete(id: string): Promise<void> {
    await del<void>(`/dashboard-templates/${id}`);
  }

  static async publish(id: string): Promise<PublishSummary> {
    return post<PublishSummary>(`/dashboard-templates/${id}/publish`, {});
  }

  static async getMetricsCatalog(): Promise<MetricCatalogItem[]> {
    return (
      (await get<MetricCatalogItem[]>(
        `/dashboard-templates/metrics-catalog`,
      )) ?? []
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static async preview(payload: any): Promise<PreviewResponse> {
    // v2: retorna {data, dashboard} flat (legacy {success,data}).

    return post<PreviewResponse>(`/dashboard-templates/preview`, payload);
  }
}
